import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import {
  ResponsiveContainer, XAxis, YAxis, Line, Area, ComposedChart, Tooltip, Legend,
} from 'recharts';
import useMobileLayout from 'Hooks/useMobileLayout';
import useManagedAccountPageEventLogger
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/managedAccountPage.eventLogging';
import useManagedAccountActivitySummaryChart from './useManagedAccountActivitySummaryChart';
import { BALANCE_CHART_NAME, CONTRIBUTION_CHART_NAME } from './consts';
import ManagedAccountActivitySummaryChartTooltip from './ManagedAccountActivitySummaryChartTooltip';


export default function ManagedAccountActivitySummaryChart({ activitySummary }) {
  const {
    balanceChartData,
    contributionChartData,
    xAxisArgs,
    yAxisArgs,
  } = useManagedAccountActivitySummaryChart(activitySummary);

  const { logActivitySummaryChartEvent } = useManagedAccountPageEventLogger();

  const isMobile = useMobileLayout();

  function handleLegendClick(_, index) {
    logActivitySummaryChartEvent('Legend', {
      name: balanceChartData.data[index].value,
    });
  }

  function handleXAxisClick() {
    logActivitySummaryChartEvent('Date Axis');
  }

  function handleYAxisClick() {
    logActivitySummaryChartEvent('Amount Axis');
  }

  if (!activitySummary) return null;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart>
        {/* Background linear gradient for balance area chart */}
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#E9F6FD" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#E9F6FD" stopOpacity={0} />
          </linearGradient>
        </defs>

        {/* Date axis */}
        <XAxis
          dataKey={xAxisArgs.dataKey}
          type={xAxisArgs.type}
          domain={xAxisArgs.domain}
          scale={xAxisArgs.scale}
          ticks={xAxisArgs.ticks}
          tickFormatter={xAxisArgs.tickFormatter}
          allowDuplicatedCategory={false}
          axisLine={{ stroke: '#EAF0F4' }}
          tickLine={false}
          onClick={handleXAxisClick}
        />

        {/* Value axis */}
        <YAxis
          width={isMobile ? 0 : 60}
          dataKey={yAxisArgs.dataKey}
          type={yAxisArgs.type}
          domain={yAxisArgs.domain}
          scale={yAxisArgs.scale}
          ticks={yAxisArgs.ticks}
          tickFormatter={yAxisArgs.tickFormatter}
          allowDuplicatedCategory={false}
          padding={{ top: 20, bottom: 20 }}
          interval="preserveStartEnd"
          minTickGap={10}
          axisLine={false}
          tickLine={false}
          tick={{ stroke: '#EAEAEA' }}
          onClick={handleYAxisClick}
        />

        {/* Balance line with area */}
        <Area
          dataKey="y"
          type="monotone"
          name={BALANCE_CHART_NAME}
          data={balanceChartData.data}
          dot={false}
          activeDot
          stroke="#007098"
          strokeWidth={3}
          fillOpacity={1}
          fill="url(#colorUv)"
        />

        {/* Contributions line */}
        <Line
          dataKey="y"
          type="monotone"
          name={CONTRIBUTION_CHART_NAME}
          data={contributionChartData.data}
          dot={false}
          activeDot
          stroke="#9C8D49"
          strokeWidth={3}
          strokeDasharray="3 3"
        />

        <Tooltip content={ManagedAccountActivitySummaryChartTooltip} wrapperStyle={{ outline: 'none' }} />
        <Legend
          iconSize={30}
          iconType="plainline"
          align="left"
          verticalAlign="top"
          onClick={handleLegendClick}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
ManagedAccountActivitySummaryChart.defaultProps = {
  activitySummary: undefined,
};
ManagedAccountActivitySummaryChart.propTypes = {
  activitySummary: PropTypes.instanceOf(fr.Resource),
};
